export type AgentChatAttachmentKind = 'image' | 'file'

export interface ClassifiedAgentChatFile {
  kind: AgentChatAttachmentKind
  mimeType: string
}

export const AGENT_CHAT_IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/heic',
  'image/heif',
] as const

export const AGENT_CHAT_FILE_MIME_TYPES = [
  'text/plain',
  'text/markdown',
  'text/html',
  'text/csv',
  'text/xml',
  'application/json',
  'application/xml',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
] as const

const IMAGE_MIME_SET = new Set<string>(AGENT_CHAT_IMAGE_MIME_TYPES)
const FILE_MIME_SET = new Set<string>(AGENT_CHAT_FILE_MIME_TYPES)

const EXTENSION_TO_MIME_TYPE: Record<string, string> = {
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.png': 'image/png',
  '.gif': 'image/gif',
  '.webp': 'image/webp',
  '.heic': 'image/heic',
  '.heif': 'image/heif',
  '.txt': 'text/plain',
  '.md': 'text/markdown',
  '.markdown': 'text/markdown',
  '.html': 'text/html',
  '.htm': 'text/html',
  '.csv': 'text/csv',
  '.json': 'application/json',
  '.xml': 'application/xml',
  '.pdf': 'application/pdf',
  '.xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
}

export const AGENT_CHAT_ACCEPTED_ATTACHMENT_TYPES = [
  ...AGENT_CHAT_IMAGE_MIME_TYPES,
  ...AGENT_CHAT_FILE_MIME_TYPES,
  ...Object.keys(EXTENSION_TO_MIME_TYPE),
].join(',')

export const AGENT_CHAT_MAX_ATTACHMENTS = 8
export const AGENT_CHAT_MAX_IMAGE_BYTES = 10 * 1024 * 1024
export const AGENT_CHAT_MAX_FILE_BYTES = 100 * 1024 * 1024
export const AGENT_CHAT_MAX_TOTAL_ATTACHMENT_BYTES = 100 * 1024 * 1024

export function classifyAgentChatFile(file: File): ClassifiedAgentChatFile | null {
  const mimeType = normalizedMimeType(file)
  if (IMAGE_MIME_SET.has(mimeType)) return { kind: 'image', mimeType }
  if (FILE_MIME_SET.has(mimeType)) return { kind: 'file', mimeType }

  return null
}

export function isPreviewableAgentChatFile(file: File): boolean {
  const mimeType = normalizedMimeType(file)
  return ['image/jpeg', 'image/png', 'image/gif', 'image/webp'].includes(mimeType)
}

export function formatAttachmentSize(bytes: number): string {
  if (bytes >= 1024 * 1024) {
    return `${(bytes / (1024 * 1024)).toLocaleString('pt-BR', {
      maximumFractionDigits: bytes >= 10 * 1024 * 1024 ? 0 : 1,
    })} MB`
  }

  return `${Math.max(1, Math.ceil(bytes / 1024)).toLocaleString('pt-BR')} KB`
}

export function validateAgentChatFiles(
  currentFiles: File[],
  incomingFiles: File[],
): { files: File[]; error: string | null } {
  if (incomingFiles.length === 0) return { files: currentFiles, error: null }

  const nextFiles = [...currentFiles]
  if (nextFiles.length + incomingFiles.length > AGENT_CHAT_MAX_ATTACHMENTS) {
    return {
      files: currentFiles,
      error: `Você pode anexar no máximo ${AGENT_CHAT_MAX_ATTACHMENTS} arquivos por mensagem.`,
    }
  }

  for (const file of incomingFiles) {
    const classification = classifyAgentChatFile(file)
    if (!classification) {
      return {
        files: currentFiles,
        error:
          `Arquivo ${file.name || 'sem nome'} não é aceito. Use imagens JPEG, PNG, GIF, WebP, HEIC ou HEIF, ` +
          'ou arquivos TXT, Markdown, HTML, CSV, JSON, XML, PDF ou XLSX.',
      }
    }

    if (file.size <= 0) {
      return {
        files: currentFiles,
        error: `Arquivo ${file.name || 'sem nome'} está vazio. Envie um arquivo com conteúdo.`,
      }
    }

    const maxBytes = classification.kind === 'image'
      ? AGENT_CHAT_MAX_IMAGE_BYTES
      : AGENT_CHAT_MAX_FILE_BYTES
    if (file.size > maxBytes) {
      const label = classification.kind === 'image' ? 'Imagem' : 'Arquivo'
      return {
        files: currentFiles,
        error: `${label} ${file.name || 'sem nome'} excede o limite de ${formatAttachmentSize(maxBytes)}.`,
      }
    }

    nextFiles.push(file)
  }

  const totalBytes = nextFiles.reduce((total, file) => total + file.size, 0)
  if (totalBytes > AGENT_CHAT_MAX_TOTAL_ATTACHMENT_BYTES) {
    return {
      files: currentFiles,
      error: `Os anexos excedem o limite total de ${formatAttachmentSize(AGENT_CHAT_MAX_TOTAL_ATTACHMENT_BYTES)} por mensagem.`,
    }
  }

  return { files: nextFiles, error: null }
}

function normalizedMimeType(file: File): string {
  const declaredType = file.type.split(';')[0]?.trim().toLowerCase()
  if (declaredType && (IMAGE_MIME_SET.has(declaredType) || FILE_MIME_SET.has(declaredType))) {
    return declaredType
  }

  const extension = file.name.toLowerCase().match(/\.[^.]+$/)?.[0] || ''
  return EXTENSION_TO_MIME_TYPE[extension] || declaredType || ''
}
